import * as Sentry from '@sentry/react';
import EventTracker from 'src/pendo/EventTracker';
import PENDO_EVENT from 'src/pendo/events.constants';
import { TextChatMessage } from '../types/text-chat';
import { ChatKeyPair, decryptChatHistory, encryptChatHistory } from '../utils/chat-crypto.util';
import { buildChatHistory, ChatHistoryEntry, parseChatHistory } from '../utils/chat-history.util';
import {
    getSwitchDeviceChatKey,
    sendSwitchDeviceChatHistory,
    SwitchChatResult,
} from './device-switch.service';

const RETRY_DELAY_MS = 1000;

async function withRetry<T extends SwitchChatResult>(call: () => Promise<T>): Promise<T> {
    const result = await call();
    if (!result.isNetworkError) {
        return result;
    }

    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));

    return call();
}

function track(event: string, requestId: string, count: number, reason: string) {
    EventTracker.trackEvent(event, { requestId, count, reason });
}

/** A failure that should not happen in normal use. Never pass anything holding decrypted chat. */
export function reportSwitchChatError(step: string, requestId: string, error: unknown) {
    console.warn(`Switch chat history: ${step} failed`, error);
    Sentry.captureException(error, { tags: { feature: 'switch-chat-history', step, requestId } });
}

/** The switch happened but its history did not arrive; the message is the server's, not chat. */
function reportSwitchChatWarning(step: string, requestId: string, message: string) {
    console.warn(`Switch chat history: ${step}`, message);
    Sentry.captureMessage(`Switch chat history: ${step}: ${message}`, {
        level: Sentry.Severity.Warning,
        tags: { feature: 'switch-chat-history', step, requestId },
    });
}

/**
 * Source side, once this device has been evicted by the switch. Best effort: the user is
 * already on the other device, so a failure is only reported, to Sentry and Pendo.
 */
export async function uploadSwitchChatHistory(
    requestId: string,
    messages: TextChatMessage[],
): Promise<void> {
    try {
        await upload(requestId, messages);
    } catch (error) {
        reportSwitchChatError('upload', requestId, error);
    }
}

async function upload(requestId: string, messages: TextChatMessage[]): Promise<void> {
    const history = buildChatHistory(messages);
    if (!history) {
        return;
    }

    const key = await withRetry(() => getSwitchDeviceChatKey(requestId));
    if (!key.data) {
        // A refusal is routine (an ordinary drop while a switch was pending); only a lost request is worth Sentry.
        if (key.isNetworkError) {
            reportSwitchChatWarning('no key', requestId, key.error);
        } else {
            console.warn('Switch chat history: no key', key.error);
        }
        track(PENDO_EVENT.SWITCH_CHAT_HISTORY_SENT, requestId, history.count, 'no_key');
        return;
    }

    let encrypted: string;
    try {
        encrypted = await encryptChatHistory(history.serialized, key.data, requestId);
    } catch (error) {
        reportSwitchChatError('encrypt', requestId, error);
        track(PENDO_EVENT.SWITCH_CHAT_HISTORY_SENT, requestId, history.count, 'encrypt_failed');
        return;
    }

    const sent = await withRetry(() =>
        sendSwitchDeviceChatHistory({ requestId, messages: encrypted }),
    );
    if (sent.error) {
        reportSwitchChatWarning('upload failed', requestId, sent.error);
    }

    track(
        PENDO_EVENT.SWITCH_CHAT_HISTORY_SENT,
        requestId,
        history.count,
        sent.error ? 'upload_failed' : '',
    );
}

/** Target side. Returns no entries when the history can't be read; the call carries on. */
export async function readSwitchChatHistory(
    encrypted: string,
    keyPair: ChatKeyPair | undefined,
    requestId: string,
): Promise<ChatHistoryEntry[]> {
    if (!keyPair) {
        track(PENDO_EVENT.SWITCH_CHAT_HISTORY_RECEIVED, requestId, 0, 'no_key');
        return [];
    }

    let plaintext: string;
    try {
        plaintext = await decryptChatHistory(encrypted, keyPair, requestId);
    } catch (error) {
        reportSwitchChatError('decrypt', requestId, error);
        track(PENDO_EVENT.SWITCH_CHAT_HISTORY_RECEIVED, requestId, 0, 'decrypt_failed');
        return [];
    }

    try {
        const entries = parseChatHistory(plaintext);
        track(PENDO_EVENT.SWITCH_CHAT_HISTORY_RECEIVED, requestId, entries.length, '');
        return entries;
    } catch (error) {
        // JSON.parse quotes its input, here the decrypted chat, so only the error's type is reported.
        const type = error instanceof Error ? error.name : typeof error;
        reportSwitchChatError(
            'parse',
            requestId,
            new Error(`Switch chat history is not in the shared format (${type})`),
        );
        track(PENDO_EVENT.SWITCH_CHAT_HISTORY_RECEIVED, requestId, 0, 'invalid_format');
        return [];
    }
}
